import { Controller } from "stimulus";

export default class extends Controller {
  static targets = [
    "dropZone",
    "fileInput",
    "selectedFile",
    "fileName",
    "fileSize",
    "submitBtn",
    "nameField",
    "statusText",
    "progressBar",
    "progressText",
  ];

  connect() {
    this.selectedFileObj = null;
  }

  openFileDialog(event) {
    // Don't open if clicking the remove button or other interactive elements
    if (event.target.closest("button")) {
      return;
    }
    this.fileInputTarget.click();
  }

  handleFileSelect(event) {
    const file = event.target.files[0];
    if (file) {
      this.selectFile(file);
    }
  }

  handleDragOver(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.add("drag-over");
    }
  }

  handleDragLeave(event) {
    event.preventDefault();
    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("drag-over");
    }
  }

  handleDrop(event) {
    event.preventDefault();
    event.stopPropagation();

    if (this.hasDropZoneTarget) {
      this.dropZoneTarget.classList.remove("drag-over");
    }

    const files = event.dataTransfer.files;
    if (files && files.length > 0) {
      this.selectFile(files[0]);
    }
  }

  selectFile(file) {
    // Validate file type
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    const validExtensions = [".pdf", ".doc", ".docx"];
    const fileExt = file.name
      .substring(file.name.lastIndexOf("."))
      .toLowerCase();

    if (!validTypes.includes(file.type) && !validExtensions.includes(fileExt)) {
      alert("Please select a PDF, DOC, or DOCX file.");
      return;
    }

    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert("File size must be less than 10MB.");
      return;
    }

    this.selectedFileObj = file;

    // Display selected file
    this.fileNameTarget.textContent = file.name;
    this.fileSizeTarget.textContent = `(${this.formatFileSize(file.size)})`;
    this.selectedFileTarget.style.display = "block";

    // Enable submit button
    this.submitBtnTarget.disabled = false;
  }

  clearFile(event) {
    event.preventDefault();
    this.selectedFileObj = null;
    this.fileInputTarget.value = "";
    this.selectedFileTarget.style.display = "none";
    this.submitBtnTarget.disabled = true;
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.selectedFileObj) {
      alert("Please select a file to upload.");
      return;
    }

    const skillName = this.nameFieldTarget.value.trim();
    if (!skillName) {
      alert("Please enter a skill name.");
      this.nameFieldTarget.focus();
      return;
    }

    // Show processing modal
    $("#processing-modal").modal("show");
    this.updateProgress(10, "Creating skill record...");

    try {
      // Step 1: Create the skill with basic info
      const formData = new FormData(document.getElementById("upload-form"));

      // Remove the file from form data (we'll upload it separately)
      formData.delete("file");

      const createResponse = await fetch("/skills", {
        method: "POST",
        headers: {
          "X-CSRF-Token": document.querySelector('[name="csrf-token"]').content,
          Accept: "application/json",
        },
        body: formData,
      });

      if (!createResponse.ok) {
        throw new Error("Failed to create skill");
      }

      const skillData = await createResponse.json();
      const skillId = skillData.id;

      this.updateProgress(30, "Preparing document upload...");

      // Step 2: Get presigned URL
      const presignResponse = await fetch(
        `/skills/${skillId}/presign_skill_sheet_upload`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            filename: this.selectedFileObj.name,
            content_type: this.selectedFileObj.type,
          }),
        }
      );

      if (!presignResponse.ok) {
        throw new Error("Failed to get upload URL");
      }

      const presignData = await presignResponse.json();

      this.updateProgress(50, "Uploading document to storage...");

      // Step 3: Upload to S3
      const uploadResponse = await fetch(presignData.url, {
        method: "PUT",
        headers: {
          "Content-Type": this.selectedFileObj.type,
        },
        body: this.selectedFileObj,
      });

      if (!uploadResponse.ok) {
        throw new Error("Failed to upload file");
      }

      this.updateProgress(70, "Submitting for AI processing...");

      // Step 4: Submit the S3 key to trigger Lambda
      const submitResponse = await fetch(
        `/skills/${skillId}/submit_skill_sheet_key`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('[name="csrf-token"]')
              .content,
          },
          body: JSON.stringify({
            s3_key: presignData.key,
            content_type: this.selectedFileObj.type,
          }),
        }
      );

      if (!submitResponse.ok) {
        throw new Error("Failed to submit for processing");
      }

      this.updateProgress(80, "AI is extracting checklist items...");

      // Step 5: Poll for completion
      await this.pollForCompletion(skillId);
    } catch (error) {
      console.error("Upload error:", error);
      $("#processing-modal").modal("hide");
      alert(
        `Upload failed: ${error.message}. Please try again or create the skill manually.`
      );
    }
  }

  async pollForCompletion(skillId) {
    let attempts = 0;
    const maxAttempts = 120; // 10 minutes max (5 second intervals)
    let lastProgress = 80;

    return new Promise((resolve, reject) => {
      const pollingInterval = setInterval(async () => {
        attempts++;

        try {
          const response = await fetch(`/skills/${skillId}/upload_status`);
          const data = await response.json();

          // Update progress based on status
          let progress = 80;
          let message = "";

          if (attempts < 4) {
            // First 15 seconds
            progress = 80 + attempts * 2;
            message = "Extracting text from document...";
          } else if (data.has_description) {
            progress = 88;
            message = "Description extracted...";
          } else if (data.has_checklist_items > 0) {
            progress = 90 + Math.min(data.has_checklist_items, 8);
            message = `Processing checklist items (${data.has_checklist_items} found)...`;
          } else {
            // Still processing, increment slowly
            progress = Math.min(lastProgress + 1, 95);
            message = `AI analyzing document (${attempts * 5}s elapsed)...`;
          }

          lastProgress = progress;
          this.updateProgress(progress, message);

          if (data.processing_complete) {
            clearInterval(pollingInterval);
            this.updateProgress(100, "Complete! Redirecting...");

            setTimeout(() => {
              $("#processing-modal").modal("hide");
              window.location.href = `/skills/${skillId}`;
            }, 800);

            resolve(data);
          } else if (attempts >= maxAttempts) {
            clearInterval(pollingInterval);
            this.updateProgress(
              95,
              "Processing is taking longer than expected..."
            );

            // Show option to view skill anyway after timeout
            if (
              confirm(
                "Processing is taking longer than expected. Would you like to view the skill now? You can refresh later to see the extracted items."
              )
            ) {
              $("#processing-modal").modal("hide");
              window.location.href = `/skills/${skillId}`;
            } else {
              $("#processing-modal").modal("hide");
            }

            reject(new Error("Processing timeout"));
          }
        } catch (error) {
          console.error("Polling error:", error);
          // Continue polling even if one request fails
        }
      }, 5000); // Poll every 5 seconds
    });
  }

  updateProgress(percent, message) {
    this.progressBarTarget.style.width = `${percent}%`;
    this.progressTextTarget.textContent = `${percent}%`;
    this.statusTextTarget.textContent = message;
  }

  formatFileSize(bytes) {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
  }
}
